import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useApi } from '../lib/useApi.ts';
import { useCanWrite } from '../lib/auth.tsx';
import { post } from '../lib/api.ts';
import { STAGE_LABEL, labelFor, type PipelineBoard, type PipelineCardApi } from '../lib/types.ts';
import { PipelineCard, type PipelineCardData } from '../components/pipeline/PipelineCard.tsx';
import { PipelineColumn } from '../components/pipeline/PipelineColumn.tsx';
import { Card, EmptyState, Skeleton } from '../components/ui/index.ts';

function toCardData(card: PipelineCardApi): PipelineCardData {
  return {
    id: card.id,
    fullName: card.full_name,
    practiceName: card.practice_name,
    dealValue: card.deal_value && Number(card.deal_value) > 0 ? card.deal_value : null,
    daysInStage: card.days_in_stage,
    risk: card.risk,
    nextEvent: card.next_event_owned === true ? (card.next_event_desc ?? 'Next step owned') : null,
  };
}

export default function Pipeline() {
  const { data, error, loading, reload } = useApi<PipelineBoard>('/pipeline');
  const canWrite = useCanWrite();
  const navigate = useNavigate();

  const [dragging, setDragging] = useState<PipelineCardApi | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A small distance threshold so a click still reads as a click and opens
  // the lead, rather than being swallowed as a zero-distance drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!saveError) return;
    const timer = setTimeout(() => setSaveError(null), 6000);
    return () => clearTimeout(timer);
  }, [saveError]);

  function onDragStart(event: DragStartEvent) {
    const card = event.active.data.current?.['card'] as PipelineCardApi | undefined;
    setDragging(card ?? null);
  }

  async function onDragEnd(event: DragEndEvent) {
    const card = dragging;
    setDragging(null);
    setOverStage(null);

    const target = event.over?.id;
    if (!card || typeof target !== 'string') return;

    const from = event.active.data.current?.['stage'] as string | undefined;
    if (from === target) return;

    try {
      // Moving a card appends a stage event. It never updates or deletes
      // one: stage_events is the append-only funnel log, and "ever reached
      // this stage" has to stay true after the fact.
      await post('/stage-events', {
        lead_id: card.id,
        stage: target,
        note: `Moved on the board from ${labelFor(STAGE_LABEL, from ?? '')}`,
      });
      reload();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Could not move that card.');
      reload();
    }
  }

  if (error) {
    return <EmptyState title="Could not load the pipeline" description={error} />;
  }

  const visible = data?.columns ?? [];
  const isEmpty = !loading && visible.every((column) => column.cards.length === 0);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tighter">Pipeline</h1>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {canWrite
              ? 'Drag a card to record a new stage. Nothing is ever overwritten.'
              : 'Read-only view.'}
          </p>
        </div>
        {saveError && <p className="text-xs text-lost">{saveError}</p>}
      </header>

      {loading && (
        <div className="flex gap-3">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-64 w-64 shrink-0" />
          ))}
        </div>
      )}

      {isEmpty && (
        <Card>
          <EmptyState
            title="Nothing in the pipeline"
            description="Every lead is either closed or has no call logged yet. Log a call from a lead's page to put them on the board."
            compact
          />
        </Card>
      )}

      {!loading && !isEmpty && (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={(event) =>
            setOverStage(typeof event.over?.id === 'string' ? event.over.id : null)
          }
          onDragCancel={() => {
            setDragging(null);
            setOverStage(null);
          }}
        >
          <div className="flex gap-3 overflow-x-auto pb-3">
            {visible.map((column) => {
              const totals = data?.totals.find((entry) => entry.stage === column.stage);
              return (
                <DroppableColumn
                  key={column.stage}
                  stage={column.stage}
                  label={column.label}
                  count={totals?.count ?? column.cards.length}
                  totalValue={totals && Number(totals.value) > 0 ? totals.value : null}
                  isOver={overStage === column.stage && dragging !== null}
                  canWrite={canWrite}
                >
                  {column.cards.map((card) => (
                    <DraggableCard
                      key={card.id}
                      card={card}
                      stage={column.stage}
                      canWrite={canWrite}
                      onOpen={() => navigate(`/leads/${card.id}`)}
                    />
                  ))}
                  {column.cards.length === 0 && (
                    <p className="px-1 py-6 text-center text-xs text-fg-faint">Nothing here</p>
                  )}
                </DroppableColumn>
              );
            })}
          </div>

          {/* Rendered outside the columns so the card follows the cursor
              without the column's overflow clipping it. */}
          <DragOverlay dropAnimation={null}>
            {dragging && (
              <div className="w-60">
                <PipelineCard data={toCardData(dragging)} dragging />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function DroppableColumn({
  stage,
  label,
  count,
  totalValue,
  isOver,
  canWrite,
  children,
}: {
  stage: string;
  label: string;
  count: number;
  totalValue: string | null;
  isOver: boolean;
  canWrite: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: stage, disabled: !canWrite });

  return (
    <div ref={setNodeRef} className="shrink-0">
      <PipelineColumn title={label} count={count} totalValue={totalValue} dropActive={isOver}>
        {children}
      </PipelineColumn>
    </div>
  );
}

function DraggableCard({
  card,
  stage,
  canWrite,
  onOpen,
}: {
  card: PipelineCardApi;
  stage: string;
  canWrite: boolean;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { card, stage },
    disabled: !canWrite,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // The original stays in place at reduced opacity while the overlay
      // follows the cursor, so the column does not reflow mid-drag.
      className={isDragging ? 'opacity-40' : undefined}
    >
      <PipelineCard data={toCardData(card)} onClick={onOpen} />
    </div>
  );
}
